import type {
  CdActionStep,
  CdAdCopy,
  CdBlueprintItem,
  CdComparisonBar,
  CdContentAdvantage,
  CdContentGap,
  CdCrawledPage,
  CdCrawlMeta,
  CdCrawlSubject,
  CdDossierHeader,
  CdFocusBar,
  CdKeywordCompare,
  CdLabeledStat,
  CdRankedKeyword,
  CdScoreCard,
  CdSocialNetwork,
  CdStrength,
  CdSwotCard,
  CdTapeRow,
  CdTechnicalCheck,
  CdThemeShare,
  CdWeakness,
} from './types';

/* ═══════════════════ Competitor Dossier ═══════════════════ */

/** Subject of the dossier (the competitor being profiled). */
export const CD_DOSSIER_HEADER: CdDossierHeader = {
  initials: 'SC',
  name: 'Smile Central Dental',
  address: '123 Main St',
  distance: '1.2 miles away',
  rating: 4.8,
  reviews: 450,
  threatScore: 82,
  moat: 'Massive Review Lead',
  segments: [
    'Corporate DSO',
    'PPO-Heavy',
    'Invisalign',
    'Dental Implants',
    'Sedation',
    'Botox',
    'General Cleanings',
  ],
};

/** SWOT insight grid. */
export const CD_SWOT_CARDS: CdSwotCard[] = [
  {
    id: 'strength',
    label: 'STRENGTHS',
    title: "Dominating Map Pack for 'Invisalign'.",
    subtitle: 'High conversion intent capture.',
  },
  {
    id: 'weakness',
    label: 'WEAKNESSES',
    title: 'Slow mobile website (35/100).',
    subtitle: 'Potential bounce risk on mobile ads.',
  },
  {
    id: 'opportunity',
    label: 'OPPORTUNITIES',
    title: "Launch 'Transparent Pricing' ads.",
    subtitle: 'Counter their frequent billing complaints.',
  },
  {
    id: 'threat',
    label: 'THREATS',
    title: 'New $99 Patient Offer launched.',
    subtitle: 'Increasing acquisition pressure.',
  },
];

/** "Tale of the Tape" head-to-head rows. */
export const CD_TAPE_ROWS: CdTapeRow[] = [
  { id: 'hours', yourClinic: '40 Hours', metric: 'Weekly Clinical Hours', competitor: '52 Hours', competitorWinning: true },
  { id: 'rating', yourClinic: '4.6 Stars', metric: 'Avg Star Rating', competitor: '4.8 Stars', competitorWinning: true },
  { id: 'reviews', yourClinic: '180', metric: 'Total Reviews', competitor: '450', competitorWinning: true },
  { id: 'velocity', yourClinic: '+3 /mo', metric: '30-Day Review Velocity', competitor: '+12 /mo', competitorWinning: true },
  { id: 'response-rate', yourClinic: '65%', metric: 'Review Response Rate', competitor: '92%', competitorWinning: true },
  { id: 'rankings', yourClinic: '2 Terms', metric: 'Top 3 Keyword Rankings', competitor: '8 Terms', competitorWinning: true },
  { id: 'ads', yourClinic: '2 Active', metric: 'Active Paid Ads', competitor: '12 Active', competitorWinning: true },
  { id: 'gbp-score', yourClinic: '85%', metric: 'GBP Completeness Score', competitor: '100%', competitorWinning: true },
  { id: 'mobile-speed', yourClinic: '65/100', metric: 'Avg Mobile Page Speed', competitor: '35/100', competitorWinning: false },
  { id: 'indexed-pages', yourClinic: '12', metric: 'Total Indexed Local Pages', competitor: '45', competitorWinning: true },
];

/** Local visibility mini-metrics. */
export const CD_LOCAL_VISIBILITY: CdLabeledStat[] = [
  { label: '30D Velocity', value: '+12' },
  { label: 'Avg Geo Pos.', value: '#2' },
  { label: 'Response Rate', value: '92%' },
];

/** Top praise themes from review mining. */
export const CD_PRAISE_THEMES: string[] = [
  'Painless Experience',
  'Friendly Support Staff',
  'Modern Equipment',
];

/** Top complaint themes from review mining. */
export const CD_COMPLAINT_THEMES: string[] = [
  'Surprise Billing',
  'Long Waiting Room Time',
  'Billing Errors',
];

/** Booking mechanics. */
export const CD_BOOKING_MECHANICS: CdLabeledStat[] = [
  { label: 'Maturity', value: 'Real-Time Scheduling' },
  { label: 'Accessibility', value: 'Spanish · Wheelchair' },
  { label: 'Financing', value: 'CareCredit, In-House' },
  { label: 'Emergency Walk-In', value: 'Same-Day Focus' },
];

/** Clinical proof points. */
export const CD_CLINICAL_PROOF: CdLabeledStat[] = [
  { label: 'High-Ticket Focus', value: 'All-on-4, Sedation' },
  { label: 'Trust Assets', value: '300+ Video Reviews' },
  { label: 'Amenities', value: 'Noise-Cancel Headphones' },
];

/** Landing-page blueprint checklist. */
export const CD_BLUEPRINT_ITEMS: CdBlueprintItem[] = [
  { label: 'Hero: Before/After Gallery', active: true },
  { label: 'Proof: Insurance-Check Modal', active: true },
  { label: 'Offer: Dr. Bio Video', active: true },
  { label: 'Book: Live Chatbot', active: false },
  { label: 'Sticky Header: Call Now CTA', active: true },
];

/* ── Google Ads tab ── */

/** Paid focus distribution. */
export const CD_FOCUS_BARS: CdFocusBar[] = [
  { id: 'implants', label: 'Implants', pct: 60, cpc: 85, tone: 'info' },
  { id: 'emergency', label: 'Emergency', pct: 40, cpc: 12, tone: 'terra' },
];

/** AI strategy bullets — Google Ads. */
export const CD_ADS_STRATEGY: string[] = [
  'Relies heavily on $99 new patient loss-leaders to drive high-volume acquisition.',
  "Uses 'Same-Day' messaging exclusively on mobile-only ad schedules for maximum intent capture.",
  "Bidding aggressively on Spanish-language 'Dentista' terms to dominate local niche market.",
  'Competitive bidding on your brand name keywords during high-peak search hours.',
];

/** Ad copy swipe file. */
export const CD_AD_COPY: CdAdCopy[] = [
  {
    id: 'implants',
    text: '"Get Your Confidence Back with Same-Day Dental Implants. $500 Off Veneers Today - Book a free consultation."',
    category: 'Implants',
    tone: 'info',
  },
  {
    id: 'emergency',
    text: '"Emergency Dentist Open Now. Same-Day Appointments for tooth pain and broken crowns."',
    category: 'Emergency',
    tone: 'terra',
  },
  {
    id: 'spanish',
    text: '"Dentista de Emergencia Cerca de Ti — Aceptamos la mayoría de seguros. Llame ahora."',
    category: 'Spanish',
    tone: 'gold',
  },
];

/* ── Local SEO tab ── */

/** Top ranked keywords for the competitor. */
export const CD_RANKED_KEYWORDS: CdRankedKeyword[] = [
  { keyword: 'Invisalign NYC', rank: '#3' },
  { keyword: 'Best Dentist NYC', rank: '#5' },
  { keyword: 'Emergency Dentist Near Me', rank: '#7' },
  { keyword: 'Dental Implants NYC', rank: '#4' },
];

/** Latest backlink assets. */
export const CD_BACKLINK_ASSETS: string[] = [
  'patch.com/new-york/dentistry-update-2024',
  'local.yahoo.com/info-2283941/smile-central',
  'health.usnews.com/dentists/nyc-top-rated',
];

/** AI strategy bullets — Local SEO. */
export const CD_SEO_STRATEGY: string[] = [
  "Building aggressive 'Service + Neighborhood' landing pages to capture hyper-local long-tail traffic.",
  'Lacks dedicated pricing or cost calculator pages for high-ticket items like full-arch implants.',
  "Missing 'Emergency Dentist' H1 tags on homepage despite bidding on those keywords in PPC.",
  'High authority local backlinks suggest strong community PR or Chamber of Commerce involvement.',
];

/* ── Social tab ── */

/** Organic social network presence. */
export const CD_SOCIAL_NETWORKS: CdSocialNetwork[] = [
  { id: 'fb', label: 'FB', active: false },
  { id: 'ig', label: 'IG', active: true },
  { id: 'tk', label: 'TK', active: false },
];

/** Social footprint mini-metrics. */
export const CD_SOCIAL_METRICS: CdLabeledStat[] = [
  { label: 'Posting Freq', value: '4x/week' },
  { label: 'Engagement', value: '4.2%' },
];

/** Content theme breakdown. */
export const CD_THEME_BREAKDOWN: CdThemeShare[] = [
  { theme: 'Clinical B&A', pct: 45 },
  { theme: 'Team Culture', pct: 30 },
  { theme: 'Video Testimonials', pct: 15 },
  { theme: 'Educational Q&A', pct: 10 },
];

/** Top local hashtags. */
export const CD_LOCAL_HASHTAGS: string[] = [
  '#MiamiVeneers',
  '#BrickellDentist',
  '#NYCDentist',
  '#SmileMakeover',
];

/** Footer crawl metadata for the dossier. */
export const CD_DOSSIER_CRAWL_META: CdCrawlMeta = {
  lastCrawled: 'April 7, 2026 at 3:42 PM',
  nextCrawl: 'Next auto-crawl in 6 days',
};

/* ═══════════════════ Competitor Crawl Report ═══════════════════ */

/** Competitors available in the crawl-report selector. */
export const CD_CRAWL_SUBJECTS: CdCrawlSubject[] = [
  { id: 'bright-smiles', name: 'Bright Smiles Co.', distance: '1.2 mi', rating: 4.2, reviews: 128, url: 'brightsmiles.com' },
  { id: 'meadowvale', name: 'Meadowvale Dental', distance: '2.1 mi', rating: 4.5, reviews: 312, url: 'meadowvaledental.ca' },
  { id: 'churchville', name: 'Churchville Family Dentistry', distance: '3.4 mi', rating: 3.9, reviews: 87, url: 'churchvilledental.com' },
  { id: 'hilton', name: 'Hilton Dental Care', distance: '4.8 mi', rating: 4.0, reviews: 203, url: 'hiltondental.ca' },
];

/** Overview score-ring tiles. */
export const CD_SCORE_CARDS: CdScoreCard[] = [
  { id: 'overall', label: 'Overall Score', score: 62, desc: 'Below average' },
  { id: 'content', label: 'Content Quality', score: 45, desc: 'Weak content' },
  { id: 'technical', label: 'Technical SEO', score: 78, desc: 'Good basics' },
  { id: 'local', label: 'Local Presence', score: 71, desc: 'Moderate' },
];

/** Competitor strengths. */
export const CD_THEIR_STRENGTHS: CdStrength[] = [
  { id: 'gbp', text: 'Strong Google Business Profile with 128 reviews' },
  { id: 'desktop-speed', text: 'Fast page load on desktop (1.8s)' },
  { id: 'blog', text: 'Active blog with 24 dental health articles' },
  { id: 'cta', text: 'Clear call-to-action on every page' },
];

/** Competitor weaknesses (your opportunities). */
export const CD_THEIR_WEAKNESSES: CdWeakness[] = [
  { id: 'no-booking', text: 'No online booking — visitors must call to schedule', severity: 'high' },
  { id: 'mobile-speed', text: 'Mobile load time is 3.8s (should be under 2.5s)', severity: 'high' },
  { id: 'no-spanish', text: 'No Spanish language pages despite 30% local demand', severity: 'medium' },
  { id: 'no-schema', text: 'Missing schema markup for dental services', severity: 'medium' },
  { id: 'stale-blog', text: "Blog hasn't been updated in 4 months", severity: 'medium' },
  { id: 'no-video', text: 'No patient testimonial videos', severity: 'low' },
];

/** Head-to-head comparison bars (you vs them). */
export const CD_COMPARISON_BARS: CdComparisonBar[] = [
  { id: 'pages-indexed', label: 'Total Pages Indexed', yours: 34, theirs: 52 },
  { id: 'blog-articles', label: 'Blog Articles', yours: 18, theirs: 24 },
  { id: 'service-pages', label: 'Service Pages', yours: 8, theirs: 12 },
  { id: 'reviews', label: 'Google Reviews', yours: 245, theirs: 128 },
  { id: 'page-load', label: 'Avg. Page Load (lower is better)', yours: 2.1, theirs: 3.8, maxVal: 5 },
  { id: 'mobile-score', label: 'Mobile Score', yours: 85, theirs: 58, maxVal: 100 },
];

/** ELVA strategic-insight counter-actions. */
export const CD_ACTION_STEPS: CdActionStep[] = [
  { num: '1', title: 'Create Spanish Pages', desc: "Capture the 30% underserved Spanish-speaking market they're ignoring" },
  { num: '2', title: "Target 'Invisalign' SEO", desc: 'Their top keyword has weak content — write a comprehensive guide to outrank' },
  { num: '3', title: 'Run Emergency Ads', desc: "They stopped bidding on 'Emergency Extraction' — grab it at low CPC" },
];

/** Content gaps — topics they cover that you don't. */
export const CD_CONTENT_GAPS: CdContentGap[] = [
  { id: 'implant-recovery', topic: 'Dental Implant Recovery Guide', traffic: '1,200/mo', difficulty: 'Medium', priority: 'High' },
  { id: 'invisalign-braces', topic: 'Invisalign vs Braces Comparison', traffic: '890/mo', difficulty: 'Low', priority: 'High' },
  { id: 'pediatric', topic: 'Pediatric Dentistry Tips', traffic: '650/mo', difficulty: 'Low', priority: 'Medium' },
  { id: 'whitening-cost', topic: 'Teeth Whitening Cost Guide', traffic: '2,100/mo', difficulty: 'High', priority: 'Medium' },
  { id: 'anxiety', topic: 'Dental Anxiety Management', traffic: '420/mo', difficulty: 'Low', priority: 'High' },
];

/** Content advantages — topics where you outperform them. */
export const CD_CONTENT_ADVANTAGES: CdContentAdvantage[] = [
  { id: 'emergency', topic: 'Emergency Dental Care Guide', yourRank: '#2', theirRank: '#14', traffic: '850/mo' },
  { id: 'family', topic: 'Family Dentistry Services', yourRank: '#3', theirRank: '#8', traffic: '720/mo' },
  { id: 'cosmetic-faq', topic: 'Cosmetic Dentistry FAQ', yourRank: '#5', theirRank: 'Not ranked', traffic: '560/mo' },
];

/** Keyword ranking comparison table. */
export const CD_KEYWORD_COMPARE: CdKeywordCompare[] = [
  { id: 'dentist-near-me', keyword: 'dentist near me', volume: '12,400', you: '#4', them: '#3', gap: -1 },
  { id: 'emergency-dentist', keyword: 'emergency dentist', volume: '8,200', you: '#2', them: '#7', gap: 5 },
  { id: 'teeth-whitening', keyword: 'teeth whitening', volume: '6,800', you: '#6', them: '#4', gap: -2 },
  { id: 'invisalign-provider', keyword: 'invisalign provider', volume: '4,500', you: '#8', them: '#2', gap: -6 },
  { id: 'family-dentist', keyword: 'family dentist', volume: '3,900', you: '#2', them: '#5', gap: 3 },
  { id: 'dental-implants-cost', keyword: 'dental implants cost', volume: '3,200', you: '—', them: '#6', gap: -99 },
  { id: 'cosmetic-dentistry', keyword: 'cosmetic dentistry', volume: '2,800', you: '#5', them: '#11', gap: 6 },
];

/** Technical SEO audit tiles. */
export const CD_TECHNICAL_CHECKS: CdTechnicalCheck[] = [
  { id: 'mobile-speed', label: 'Page Speed (Mobile)', value: '3.8s', status: 'bad', benchmark: 'Under 2.5s' },
  { id: 'desktop-speed', label: 'Page Speed (Desktop)', value: '1.8s', status: 'good', benchmark: 'Under 3.0s' },
  { id: 'https', label: 'HTTPS Security', value: 'Secure', status: 'good', benchmark: 'Required' },
  { id: 'responsive', label: 'Mobile Responsive', value: 'Partial', status: 'warning', benchmark: 'Full responsive' },
  { id: 'schema', label: 'Schema Markup', value: 'Missing', status: 'bad', benchmark: 'Dental schema' },
  { id: 'sitemap', label: 'Sitemap', value: 'Present', status: 'good', benchmark: 'Required' },
];

/** AI advantage copy — Technical tab. */
export const CD_TECHNICAL_INSIGHT =
  'Their slow mobile speed (3.8s) and missing schema markup are costing them rankings. Google prioritizes mobile-first indexing — their 3.8s load time puts them in the bottom 30% of dental sites. Make sure your site stays under 2.5s and has proper dental practice schema to maintain your technical edge.';

/** Crawled pages analysis. */
export const CD_CRAWLED_PAGES: CdCrawledPage[] = [
  { id: 'invisalign', url: '/invisalign', title: 'Invisalign Treatment | Bright Smiles', type: 'Service', seoScore: 82, issues: 1, traffic: '450/mo' },
  { id: 'teeth-whitening', url: '/teeth-whitening', title: 'Professional Teeth Whitening', type: 'Service', seoScore: 74, issues: 2, traffic: '380/mo' },
  { id: 'implant-guide', url: '/blog/dental-implant-guide', title: 'Complete Guide to Dental Implants', type: 'Blog', seoScore: 68, issues: 3, traffic: '1,200/mo' },
  { id: 'emergency-dental', url: '/emergency-dental', title: 'Emergency Dental Services', type: 'Service', seoScore: 41, issues: 5, traffic: '290/mo' },
  { id: 'kids-dental', url: '/blog/kids-dental-health', title: "Kids Dental Health: A Parent's Guide", type: 'Blog', seoScore: 55, issues: 4, traffic: '650/mo' },
  { id: 'cosmetic-dentistry', url: '/cosmetic-dentistry', title: 'Cosmetic Dentistry Services', type: 'Service', seoScore: 38, issues: 6, traffic: '180/mo' },
  { id: 'about', url: '/about', title: 'About Our Practice', type: 'Landing', seoScore: 72, issues: 1, traffic: '120/mo' },
];

/** Total crawled-page count surfaced in copy. */
export const CD_CRAWL_PAGE_COUNT = 52;

/** Footer crawl metadata for the crawl report. */
export const CD_CRAWL_META: CdCrawlMeta = {
  lastCrawled: 'April 7, 2026 at 3:42 PM',
  nextCrawl: 'Next auto-crawl in 6 days',
};
